import * as React from 'react';
import { useContext, useMemo } from 'react';
import { useSignals } from '@preact/signals-react/runtime';
import { useComputed, useSignal } from '@preact/signals-react';
import styled from 'styled-components';
import {
  EditorChangeKind,
  EditorDeclarativeBlock,
  HtmlTransformKind,
} from '@/webapi/use-experience-api';
import {
  ContextualAttachment,
  SelectedAgent,
} from '@/features/ai-agents/shared/models';
import { VSpace } from '@/components/spacing';
import {
  AiComponentContextProps,
  AiComponentContextProvider,
} from '@/features/editor/widgets/shared/ai-editing-layout/context';
import { AiEditorTabContainer } from '@/features/editor/widgets/shared/ai-editing-layout/tab-container';
import { DeviceType } from '@/utils/definitions';
import { EditorContext } from '@/features/editor/context/editor-context';
import { AiOriginAction } from '@/pkg/sdk';
import { truncate } from '@/utils/types';

export interface AiSectionEditorProps {
  selector: string;
  widgetId?: string;
  loomiId?: string;
  originalChange?: EditorDeclarativeBlock;
}

export function AiSectionEditor({
  selector,
  originalChange,
}: AiSectionEditorProps) {
  useSignals();

  const {
    devicePreview: {
      editorState: { device },
      iframeRef,
    },
    transpiler: { asCompoundChange },
  } = useContext(EditorContext);

  const initChange = () =>
    asCompoundChange(
      EditorChangeKind.EDIT_COMPOUND,
      HtmlTransformKind.UNKNOWN,
      selector,
      selector,
      ``,
    );

  const newChange = useSignal(originalChange || initChange());

  const ctxAttachments: ContextualAttachment[] = useMemo(() => {
    const element = (
      iframeRef.current as HTMLIFrameElement
    ).contentDocument.querySelector(`${selector}`);
    const payload = element?.innerHTML || ``;
    return [
      {
        id: selector,
        caption: truncate(selector, 25),
        payload,
      },
    ];
  }, [selector]);

  const props = useComputed(
    () =>
      ({
        originalChange: newChange.value,
        selectedAgent: {
          id: `section-modifier`,
          type: `agent`,
        } as SelectedAgent,
        originAction: AiOriginAction.SECTION_MODIFIER,
        starters: () => null,
        initialContextualAttachments: ctxAttachments,
      } as AiComponentContextProps),
  );

  return (
    <Wrapper device={device}>
      <VSpace value={2} />

      <AiComponentContextProvider
        key="ai-section-editor-ctx-provider"
        props={props}
      >
        <AiEditorTabContainer />
      </AiComponentContextProvider>
      <VSpace value={2} />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: ${(props: { device: DeviceType }) =>
    props.device === DeviceType.Desktop ? `100%` : `100%`};

  display: flex;
  flex-direction: column;
`;
